import { Button, Space, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import Table from '../../../../shared/components/table/table';
import { ClockHistoryIcon } from '../../../../shared/icons/clock-history-icon';
import { DocumentAddIcon } from '../../../../shared/icons/document-add-icon';
import { PencilSquareIcon } from '../../../../shared/icons/pencil-square-icon';
import { SendIcon } from '../../../../shared/icons/send-icon';
import { TrashIcon } from '../../../../shared/icons/trash-icon';
import { openModal } from '../../../ui/store/ui-slice';
import {
  customerFormDeleteModalId,
  customerFormSendDocumentModalId,
  customerSendFileModalId,
  customersFormModalId,
} from '../../constants/constants';
import { Customer } from '../../models/customer';
import { setActiveCustomerId } from '../../store/customers-slice';
import styles from '../../../../styles/components/scan-sign-requests-table.module.css';

interface Props {
  isCustomerDataFetching: boolean;
  customersDataSource: Omit<Customer, 'groups'>[];
}

const CustomersTable = ({ isCustomerDataFetching, customersDataSource }: Props): JSX.Element => {
  const { t } = useTranslation(['customers', 'common']);
  const dispatch = useDispatch();

  const openCustomerModal = (modalId: string, activeCustomerId: string) => {
    dispatch(openModal(modalId));
    dispatch(setActiveCustomerId(activeCustomerId));
  };

  const columns: ColumnsType<Omit<Customer, 'groups'>> = [
    {
      title: t('label.name', { ns: 'common' }),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('label.surname', { ns: 'common' }),
      dataIndex: 'surname',
      key: 'surname',
    },
    {
      title: t('label.email', { ns: 'common' }),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('label.phone', { ns: 'common' }),
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: t('label.created', { ns: 'common' }),
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: t('label.actions', { ns: 'common' }),
      key: 'actions',
      fixed: 'right',
      width: 254,
      render: (_, { id }) => (
        <Space size="small">
          <Tooltip mouseEnterDelay={0.5} title={t('action.edit', { ns: 'common' })}>
            <Button
              onClick={() => openCustomerModal(customersFormModalId, id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper>
                  <PencilSquareIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.sendFile')}>
            <Button
              onClick={() => openCustomerModal(customerSendFileModalId, id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper>
                  <SendIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.history', { ns: 'common' })}>
            <Link to={Routes.CustomerTraceLogs.replace(':id', id)}>
              <Button
                type="text"
                shape="circle"
                icon={
                  <IconWrapper>
                    <ClockHistoryIcon />
                  </IconWrapper>
                }
              />
            </Link>
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.sendDocumentToSign')}>
            <Button
              onClick={() => openCustomerModal(customerFormSendDocumentModalId, id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper>
                  <DocumentAddIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.delete', { ns: 'common' })}>
            <Button
              onClick={() => openCustomerModal(customerFormDeleteModalId, id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper
                  textProps={{
                    type: 'warning',
                  }}
                >
                  <TrashIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
  return (
    <div className={styles.tableContainer}>
      <Table
        tableProps={{
          rowKey: ({ id }: Customer) => id,
          scroll: {
            y: '100%',
          },
          loading: isCustomerDataFetching,
          dataSource: customersDataSource,
          columns,
        }}
      />
    </div>
  );
};

export default CustomersTable;
